import { AuthService } from '../services/AuthService';
import { GitHubService } from '../services/GitHubService';
import { parseGithubRepoUrl } from '../parseGithubRepoUrl';
import type { GitHubConfig } from '../types';
import {
  readAuthUrlPrefill,
  sampleSignInHref,
  sampleSignInNetlifyUrl,
  sampleSignInUrlForDisplay,
  stripPatFromUrl,
} from '../utils/authUrlParams';
import { escapeAttr } from '../utils/escapeHtml';

export class AuthScreen {
  private readonly onAuthenticated: (config: GitHubConfig) => void;

  constructor(onAuthenticated: (config: GitHubConfig) => void) {
    this.onAuthenticated = onAuthenticated;
  }

  render(container: HTMLElement): void {
    const urlPrefill = readAuthUrlPrefill();
    const legacyPat = urlPrefill.pat ? null : AuthService.readLegacyPat();
    const repoInitial = urlPrefill.repo ?? '';
    const branchInitial = urlPrefill.branch ?? '';
    const patInitial = urlPrefill.pat ?? legacyPat ?? '';

    container.innerHTML = `
      <div class="auth-screen">
        <div class="auth-card">
          <img class="auth-logo" src="/codenesium-favicon.png" alt="" width="48" height="48" decoding="async" />
          <h1>Codenesium</h1>
          <p>Paste a link to your GitHub repository and a Personal Access Token with <code>repo</code> scope (contents read/write). The app only reads and writes files under <code>database/</code>; you may organize pages and subfolders there however you like.</p>
          <p class="auth-hint">
            Create a token at
            <a href="https://github.com/settings/tokens" target="_blank" rel="noopener">
              github.com/settings/tokens
            </a>.
          </p>
          <p class="auth-hint auth-sample-hint">
            Sample:
            <a href="${escapeAttr(sampleSignInHref())}" title="${escapeAttr(sampleSignInNetlifyUrl())}">DailyLifePlayground</a>
            <span class="auth-sample-url">(<code>${escapeAttr(sampleSignInUrlForDisplay())}</code>)</span>
          </p>
          <div class="auth-fields">
            <div class="auth-field">
              <label for="repo-url-input">Repository</label>
              <input id="repo-url-input" type="text" placeholder="https://github.com/you/your-repo" autocomplete="off" spellcheck="false" value="${escapeAttr(repoInitial)}" />
            </div>
            <div class="auth-field">
              <label for="branch-input">Branch <span class="auth-optional">(optional)</span></label>
              <input id="branch-input" type="text" placeholder="main" autocomplete="off" spellcheck="false" value="${escapeAttr(branchInitial)}" />
            </div>
            <div class="auth-field">
              <label for="pat-input">Personal access token</label>
              <input
                id="pat-input"
                type="password"
                placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                autocomplete="off"
                spellcheck="false"
              />
            </div>
          </div>
          <div class="auth-actions">
            <button id="pat-submit" class="btn-primary">Connect</button>
          </div>
          <p id="auth-error" class="auth-error hidden"></p>
        </div>
      </div>
    `;

    const repoUrlIn = container.querySelector<HTMLInputElement>('#repo-url-input')!;
    const branchIn = container.querySelector<HTMLInputElement>('#branch-input')!;
    const patIn = container.querySelector<HTMLInputElement>('#pat-input')!;
    const button = container.querySelector<HTMLButtonElement>('#pat-submit')!;
    const error = container.querySelector<HTMLElement>('#auth-error')!;

    if (patInitial) patIn.value = patInitial;

    stripPatFromUrl();

    if (!repoUrlIn.value && !patIn.value) patIn.focus();
    else if (!repoUrlIn.value) repoUrlIn.focus();
    else if (!patIn.value) patIn.focus();
    else repoUrlIn.focus();

    const submit = async () => {
      const pat = patIn.value.trim();
      const branch = branchIn.value.trim() || 'main';
      const parsed = parseGithubRepoUrl(repoUrlIn.value);

      if (!parsed) {
        error.textContent =
          'Enter a github.com repository URL (https://github.com/owner/repo), SSH (git@github.com:owner/repo.git), or shorthand owner/repo.';
        error.classList.remove('hidden');
        return;
      }
      if (!pat) {
        error.textContent = 'Please enter a token.';
        error.classList.remove('hidden');
        return;
      }

      const config: GitHubConfig = { pat, owner: parsed.owner, repo: parsed.repo, branch };
      error.classList.add('hidden');
      button.disabled = true;
      button.textContent = 'Connecting…';

      try {
        const github = new GitHubService(config);
        await github.ensureBranchExists();
        AuthService.save(config);
        this.onAuthenticated(config);
      } catch (err) {
        error.textContent = (err as Error).message || 'Could not connect to GitHub.';
        error.classList.remove('hidden');
        button.disabled = false;
        button.textContent = 'Connect';
      }
    };

    button.addEventListener('click', () => void submit());
    patIn.addEventListener('keydown', e => {
      if (e.key === 'Enter') void submit();
    });
    repoUrlIn.addEventListener('keydown', e => {
      if (e.key === 'Enter') void submit();
    });
    branchIn.addEventListener('keydown', e => {
      if (e.key === 'Enter') void submit();
    });
  }
}
